import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// エージェント一覧ページのコンテナ。状態管理・データ取得・各種ハンドラ（executeAgent/deleteAgent など）を保持し、
// 表示は子コンポーネント（AgentFilterBar / AgentList / PopularAgentsRanking / AgentDetailModal）に委譲する。
import { useState, useEffect } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useAuth } from '../../hooks/useAuth';
import AgentModal from '../../components/AgentModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import PageShell from '../../components/PageShell';
import toast from 'react-hot-toast';
import { FaRobot, FaPlus, FaLock } from 'react-icons/fa';
import { getStatusText, mapDiveAgentToAIAgent } from './utils';
import AgentFilterBar from './components/AgentFilterBar';
import AgentList from './components/AgentList';
import PopularAgentsRanking from './components/PopularAgentsRanking';
import AgentDetailModal from './components/AgentDetailModal';
const HomePage = () => {
    const { token, logout, isAuthenticated, isLoading: authLoading, isReady } = useAuth();
    const [agents, setAgents] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showDiveModal, setShowDiveModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    // Phase 18.1-3 Step 10: 拡張フィルタリング機能（すべてDiveエージェント統一）
    const [selectedStatus, setSelectedStatus] = useState('all'); // all, active, inactive, healthy, warning, error
    const [sortBy, setSortBy] = useState('name'); // name, created, executions, status
    const [sortOrder, setSortOrder] = useState('asc');
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [showAgentDialog, setShowAgentDialog] = useState(false);
    const [agentDialogMode, setAgentDialogMode] = useState('view');
    const [chatMessage, setChatMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isExecuting, setIsExecuting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [agentToDelete, setAgentToDelete] = useState(null);
    useEffect(() => {
        if (isAuthenticated && token && !authLoading && isReady) {
            loadDashboardData();
            const interval = setInterval(loadDashboardData, 30000);
            return () => {
                clearInterval(interval);
            };
        }
        else {
            if (!authLoading) {
                setLoading(false);
            }
        }
    }, [isAuthenticated, token, authLoading, isReady]);
    const loadDashboardData = async () => {
        try {
            setError('');
            // apiClientにトークンが設定されているかを確認
            if (!apiClient.token) {
                console.warn('ApiClient token not set - skipping API calls');
                return;
            }
            let agentData = [];
            // Local（Fuchi/Redis）
            const localPromise = (async () => {
                try {
                    const response = await apiClient.get('/api/agents?limit=50&offset=0');
                    if (!response.success) {
                        if (response.error?.includes('Authentication required')) {
                            setError('認証エラーが発生しました。ページを再読み込みしてください。');
                            return [];
                        }
                        throw new Error(response.error || 'Local API call failed');
                    }
                    const localAgents = (response.data?.agents || response.data || []);
                    // ローカルエージェントにisDive: falseを設定
                    return localAgents.map(agent => ({
                        ...agent,
                        isDive: false
                    }));
                }
                catch (e) {
                    console.warn('Local agents fetch error (ignored in merged view):', e);
                    return [];
                }
            })();
            // Dive（Mizu）
            const divePromise = (async () => {
                try {
                    const res = await apiClient.mizu.dive.listAgents();
                    const hasSuccessFlag = typeof res === 'object' && res !== null && ('success' in res);
                    if (hasSuccessFlag && res.success === false) {
                        throw new Error(res.error || 'エージェント一覧の取得に失敗しました');
                    }
                    const payload = hasSuccessFlag ? res.data : res;
                    const raw = payload?.agents || payload?.data || payload || [];
                    const mapped = raw.map(mapDiveAgentToAIAgent);
                    return mapped;
                }
                catch (e) {
                    console.warn('Dive agents fetch error (ignored in merged view):', e);
                    return [];
                }
            })();
            const [localAgents, diveAgents] = await Promise.all([localPromise, divePromise]);
            agentData = [...localAgents, ...diveAgents];
            setAgents(agentData);
            // メトリクス計算（stats未定義チェック追加）
            const totalAgents = agentData.length;
            const activeAgents = agentData.filter((agent) => agent.isActive).length;
            const totalExecutions = agentData.reduce((sum, agent) => sum + (agent.stats?.totalCalls || 0), 0);
            const totalSuccessful = agentData.reduce((sum, agent) => sum + (agent.stats?.successfulCalls || 0), 0);
            const successRate = totalExecutions > 0 ? (totalSuccessful / totalExecutions) * 100 : 0;
            const avgLatency = agentData.length > 0
                ? agentData.reduce((sum, agent) => sum + (agent.stats?.averageLatency || 0), 0) / agentData.length
                : 0;
            const topAgents = agentData
                .filter((agent) => agent.stats && agent.stats.totalCalls > 0)
                .sort((a, b) => (b.stats?.totalCalls || 0) - (a.stats?.totalCalls || 0))
                .slice(0, 5)
                .map((agent) => ({
                name: agent.name,
                executions: agent.stats?.totalCalls || 0,
                successRate: agent.stats && agent.stats.totalCalls > 0 ? (agent.stats.successfulCalls / agent.stats.totalCalls) * 100 : 0
            }));
            setMetrics({
                totalAgents,
                activeAgents,
                totalExecutions,
                successRate,
                averageLatency: avgLatency,
                topAgents
            });
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'ダッシュボードデータの取得に失敗しました';
            setError(errorMessage);
            console.error('Dashboard load error:', err);
        }
        finally {
            setLoading(false);
        }
    };
    // Phase 18.1-3 Step 10: 拡張フィルタリング＆ソート機能（すべてDiveエージェント統一）
    const filteredAndSortedAgents = agents
        .filter(agent => {
        const matchesSearch = searchTerm === '' ||
            agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (agent.description && agent.description.toLowerCase().includes(searchTerm.toLowerCase()));
        // ステータスフィルタ  
        let matchesStatus = true;
        if (selectedStatus !== 'all') {
            const statusText = getStatusText(agent);
            switch (selectedStatus) {
                case 'active':
                    matchesStatus = agent.isActive;
                    break;
                case 'inactive':
                    matchesStatus = !agent.isActive;
                    break;
                case 'healthy':
                    matchesStatus = statusText === '正常' || statusText === '未使用';
                    break;
                case 'warning':
                    matchesStatus = statusText === '注意';
                    break;
                case 'error':
                    matchesStatus = statusText === '問題あり';
                    break;
            }
        }
        return matchesSearch && matchesStatus;
    })
        .sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
            case 'name':
                comparison = a.name.localeCompare(b.name);
                break;
            case 'created':
                comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                break;
            case 'executions':
                comparison = (a.stats?.totalCalls || 0) - (b.stats?.totalCalls || 0);
                break;
            case 'status':
                const statusA = getStatusText(a);
                const statusB = getStatusText(b);
                comparison = statusA.localeCompare(statusB);
                break;
            default:
                comparison = 0;
        }
        return sortOrder === 'asc' ? comparison : -comparison;
    });
    const filteredAgents = filteredAndSortedAgents;
    // ページネーション設定（Chrome入力レスポンス問題対策）
    const ITEMS_PER_PAGE = 50;
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.ceil(filteredAgents.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedAgents = filteredAgents.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    // 検索やフィルター変更時にページをリセット（Phase 18.1-3 Step 10: 拡張フィルター対応）
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedStatus, sortBy, sortOrder]);
    const handleAgentSelect = (agent, mode = 'view') => {
        setSelectedAgent(agent);
        setAgentDialogMode(mode);
        setShowAgentDialog(true);
        setChatHistory([]);
        setChatMessage('');
    };
    const executeAgent = async () => {
        if (!selectedAgent || !chatMessage.trim())
            return;
        setIsExecuting(true);
        const startTime = Date.now();
        try {
            // デバッグ: エージェント情報を出力
            console.log('実行対象エージェント:', {
                id: selectedAgent.id,
                name: selectedAgent.name,
                category: selectedAgent.category,
                isDive: selectedAgent.isDive,
                endpointPath: selectedAgent.endpointPath,
                endpoint: selectedAgent.endpoint
            });
            // Diveエージェント判定（deleteAgent関数と同様のロジック）
            const isDiveAgent = selectedAgent.isDive ||
                selectedAgent.category === 'Dive' ||
                !selectedAgent.endpointPath ||
                selectedAgent.endpointPath === '';
            console.log('Diveエージェント判定:', {
                isDiveFlag: selectedAgent.isDive,
                categoryCheck: selectedAgent.category === 'Dive',
                endpointCheck: !selectedAgent.endpointPath || selectedAgent.endpointPath === '',
                finalResult: isDiveAgent
            });
            let response;
            if (isDiveAgent) {
                // Diveエージェント: Mizu Dive API経由で実行
                console.log('Diveエージェント実行: Mizu Dive API使用');
                // Step 1: Thread作成（user_id必須パラメータ追加）
                const threadResponse = await fetch('/api/mizu/dive/threads', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        agent_id: selectedAgent.id,
                        user_id: (() => {
                            // ユーザーIDの確実な取得（複数のソースを試行）
                            try {
                                const userInfo = JSON.parse(localStorage.getItem('teico_user') || '{}');
                                return userInfo.id || userInfo.username || userInfo.user_id || localStorage.getItem('teico_user') || 'long';
                            }
                            catch {
                                return localStorage.getItem('teico_user') || 'long'; // フォールバック
                            }
                        })(),
                        name: `寿司製造相談_${new Date().getTime()}`,
                        title: `寿司製造実行率改善相談 - ${new Date().toLocaleString('ja-JP')}`, // 必須: Thread Title
                        description: `${selectedAgent.name}との対話セッション`, // オプション: 詳細説明
                        created_by: (() => {
                            // Created By の確実な取得
                            try {
                                const userInfo = JSON.parse(localStorage.getItem('teico_user') || '{}');
                                return userInfo.username || userInfo.id || localStorage.getItem('teico_user') || 'fuchi-user';
                            }
                            catch {
                                return localStorage.getItem('teico_user') || 'fuchi-user';
                            }
                        })(), // 必須: Thread作成者
                        metadata: {
                            purpose: '対話テスト',
                            agent_name: selectedAgent.name,
                            agent_category: selectedAgent.category,
                            created_from: 'fuchi-dashboard',
                            session_type: 'interactive_chat',
                            timestamp: new Date().toISOString()
                        }
                    })
                });
                if (!threadResponse.ok) {
                    throw new Error(`Thread作成に失敗しました: ${threadResponse.status}`);
                }
                const threadResult = await threadResponse.json();
                const threadId = threadResult.data?.thread_id || threadResult.data?.id;
                if (!threadId) {
                    throw new Error('Thread IDの取得に失敗しました');
                }
                console.log('Thread作成成功:', threadId);
                // Step 2: Run実行（必須フィールド messages 追加）
                // Accept: text/event-stream により Mizu が SSE ストリーミングを返す
                response = await fetch('/api/mizu/dive/runs', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream'
                    },
                    body: JSON.stringify({
                        thread_id: threadId,
                        agent_id: selectedAgent.id,
                        user_id: (() => {
                            try {
                                const userInfo = JSON.parse(localStorage.getItem('teico_user') || '{}');
                                return userInfo.id || userInfo.username || userInfo.user_id || 'long';
                            }
                            catch {
                                return 'long';
                            }
                        })(),
                        input: chatMessage.trim(),
                        messages: [
                            {
                                type: "human",
                                content: chatMessage.trim(),
                                metadata: {
                                    timestamp: new Date().toISOString(),
                                    source: "fuchi-dashboard",
                                    agent_name: selectedAgent.name
                                }
                            }
                        ],
                        metadata: {
                            session_type: "interactive_chat",
                            client: "fuchi-dashboard",
                            agent_category: selectedAgent.category,
                            execution_context: "user_interaction"
                        }
                    })
                });
                console.log('Dive Run実行API呼び出し完了:', response.status);
                // Step 3: Run結果取得（Dive API専用）
                if (!response.ok) {
                    // Google MCP サーバー再認証が必要な場合は専用エラーメッセージを表示
                    const errorBody = await response.json().catch(() => ({}));
                    if (errorBody?.mcp_reauth_required && errorBody?.mcp_reauth_servers?.length > 0) {
                        const servers = errorBody.mcp_reauth_servers.join(', ');
                        throw new Error(`Google MCP サーバー（${servers}）のトークンが無効になりました。エージェント設定画面でGoogle MCP認証をやり直してください。`);
                    }
                    throw new Error(`Run実行に失敗しました: ${response.status}`);
                }
                // SSE ストリーミング分岐: content-type が text/event-stream なら逐次パース
                // Mizu が SSE を返せなかった場合（例: Accept を無視した場合）は従来のポーリングに fallback
                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('text/event-stream') && response.body) {
                    // content（string / Gemini array / 混合配列）からテキスト抽出
                    const extractTextFromContent = (content) => {
                        if (typeof content === 'string')
                            return content;
                        if (Array.isArray(content)) {
                            return content.map((item) => {
                                if (typeof item === 'string')
                                    return item;
                                if (item.type === 'text' || item.text)
                                    return item.text || item.content || '';
                                return '';
                            }).join('');
                        }
                        return '';
                    };
                    // ユーザーメッセージと空のAIメッセージを chatHistory に追加（ストリーム受信で逐次更新する）
                    const userMessage = {
                        role: 'user',
                        message: chatMessage,
                        timestamp: new Date().toISOString()
                    };
                    const initialAgentMessage = {
                        role: 'agent',
                        message: '',
                        timestamp: new Date().toISOString(),
                        responseTime: 0,
                        isStreaming: true
                    };
                    setChatHistory(prev => [...prev, userMessage, initialAgentMessage]);
                    setChatMessage('');
                    const reader = response.body.getReader();
                    const decoder = new TextDecoder();
                    let buffer = '';
                    let accumulated = '';
                    let streamDone = false;
                    let streamError = null;
                    const updateLastAgentMessage = (text) => {
                        setChatHistory(prev => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            if (last && last.role === 'agent') {
                                next[next.length - 1] = { ...last, message: text };
                            }
                            return next;
                        });
                    };
                    try {
                        while (!streamDone) {
                            const { value, done } = await reader.read();
                            if (done)
                                break;
                            buffer += decoder.decode(value, { stream: true });
                            // SSE frames are separated by double newline
                            let frameEnd;
                            while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
                                const frame = buffer.slice(0, frameEnd);
                                buffer = buffer.slice(frameEnd + 2);
                                const dataLine = frame.split('\n').find(l => l.startsWith('data:'));
                                if (!dataLine)
                                    continue;
                                const payload = dataLine.slice(5).trim();
                                if (!payload || payload === '[DONE]')
                                    continue;
                                let event;
                                try {
                                    event = JSON.parse(payload);
                                }
                                catch (e) {
                                    console.warn('SSE frame parse error:', e, payload);
                                    continue;
                                }
                                if (event.type === 'token' && typeof event.content === 'string') {
                                    accumulated += event.content;
                                    updateLastAgentMessage(accumulated);
                                }
                                else if (event.type === 'message' && event.content) {
                                    // 完全メッセージイベント: messages 配列から最後のAIテキストを抽出して置換
                                    let replacement = '';
                                    if (event.content.messages && Array.isArray(event.content.messages)) {
                                        const aiMessages = event.content.messages.filter((m) => m.type === 'ai' && extractTextFromContent(m.content).trim() !== '');
                                        const lastAi = aiMessages[aiMessages.length - 1];
                                        if (lastAi)
                                            replacement = extractTextFromContent(lastAi.content);
                                    }
                                    else {
                                        replacement = extractTextFromContent(event.content);
                                    }
                                    if (replacement) {
                                        accumulated = replacement;
                                        updateLastAgentMessage(accumulated);
                                    }
                                }
                                else if (event.type === 'error') {
                                    streamError = event.error || 'Streaming error';
                                    streamDone = true;
                                    break;
                                }
                                else if (event.type === 'end') {
                                    streamDone = true;
                                    break;
                                }
                            }
                        }
                    }
                    finally {
                        try {
                            reader.releaseLock();
                        }
                        catch { }
                        // 念のため isStreaming を必ず解除（end未受信で切れた場合も含む）
                        setChatHistory(prev => {
                            const next = [...prev];
                            const last = next[next.length - 1];
                            if (last && last.role === 'agent' && last.isStreaming) {
                                next[next.length - 1] = { ...last, isStreaming: false };
                            }
                            return next;
                        });
                    }
                    if (streamError) {
                        throw new Error(streamError);
                    }
                    // responseTime を最終更新
                    const streamResponseTime = Date.now() - startTime;
                    setChatHistory(prev => {
                        const next = [...prev];
                        const last = next[next.length - 1];
                        if (last && last.role === 'agent') {
                            next[next.length - 1] = { ...last, responseTime: streamResponseTime };
                        }
                        return next;
                    });
                    loadDashboardData();
                    return; // SSE パス完了
                }
                const runResponse = await response.json();
                console.log('Run実行レスポンス:', runResponse);
                // Run IDを取得
                const runId = runResponse.data?.run_id || runResponse.run_id;
                if (!runId) {
                    throw new Error('Run ID取得に失敗しました');
                }
                console.log('Run ID取得成功:', runId);
                // Step 4: Run結果ポーリング（AI完了まで待機）
                let attempts = 0;
                const maxAttempts = 30;
                let runResult = null;
                while (attempts < maxAttempts) {
                    // 最初の数回は短い間隔、その後長い間隔
                    const waitTime = attempts < 3 ? 1000 : attempts < 10 ? 2000 : 3000;
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                    attempts++;
                    const statusResponse = await fetch(`/api/mizu/dive/runs/${runId}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    if (statusResponse.ok) {
                        runResult = await statusResponse.json();
                        // 🔍 【詳細デバッグ】完全レスポンス構造ダンプ
                        // 🔍 【メッセージ詳細分析】
                        if (runResult.data?.messages && Array.isArray(runResult.data.messages)) {
                            runResult.data.messages.forEach((msg, idx) => {
                            });
                        }
                        else {
                        }
                        console.log(`Run状態確認 (試行${attempts}):`, runResult);
                        // 🔍 詳細レスポンス構造確認用ログ
                        console.log('runResult.data詳細:', JSON.stringify(runResult.data, null, 2));
                        console.log('runResult.data?.status:', runResult.data?.status);
                        console.log('runResult.status:', runResult.status);
                        // contentからテキストを抽出するヘルパー（string / Gemini array / 混合配列 に対応）
                        // Geminiは content を以下の形式で返す:
                        //   - string: "テキスト"
                        //   - array: [{ type: "text", text: "..." }, "生の文字列", ...]  ← 混合あり
                        const extractTextFromContent = (content) => {
                            if (typeof content === 'string')
                                return content;
                            if (Array.isArray(content)) {
                                return content.map((item) => {
                                    if (typeof item === 'string')
                                        return item; // 生の文字列要素
                                    if (item.type === 'text' || item.text)
                                        return item.text || item.content || '';
                                    return '';
                                }).join('');
                            }
                            return '';
                        };
                        // AI完了判定（Kusanagi実際のレスポンス対応）
                        // 重要: 最後のメッセージがAIかつtool_callsを含まない場合のみ完了とみなす
                        // 最後がtool結果の場合 = AIがまだツール結果を元に最終回答を生成中
                        // tool_calls付きAIメッセージ = AIがまだツール呼び出し中（中間ステップ）
                        const hasAIResponse = (() => {
                            if (!runResult.data?.messages || !Array.isArray(runResult.data.messages) || runResult.data.messages.length === 0) {
                                return false;
                            }
                            const msgs = runResult.data.messages;
                            const lastMsg = msgs[msgs.length - 1];
                            const hasContent = lastMsg.type === 'ai' && extractTextFromContent(lastMsg.content).trim() !== '';
                            // tool_calls が存在する場合は中間ステップ（まだツール実行中）
                            const hasToolCalls = lastMsg.tool_calls && Array.isArray(lastMsg.tool_calls) && lastMsg.tool_calls.length > 0;
                            return hasContent && !hasToolCalls;
                        })();
                        const hasCompletedStatus = (
                        // status === 'OK' は除外: KusanagiのHTTPレベルのOKであり実行完了を意味しない
                        runResult.data?.status === 'COMPLETED' ||
                            runResult.data?.status === 'completed' ||
                            runResult.data?.status === 'FINISHED' ||
                            runResult.data?.state === 'COMPLETED' ||
                            runResult.data?.run_status === 'COMPLETED' ||
                            runResult.status === 'COMPLETED'
                        // output/result/response による早期完了判定も除外: 中間出力で誤検知するため
                        );
                        // status=completedで確定完了、またはstatus=OKかつtool_callsなしの最終AIメッセージで完了
                        const hasOkWithFinalAI = runResult.data?.status === 'OK' && hasAIResponse;
                        const isCompleted = hasCompletedStatus || hasOkWithFinalAI;
                        const isFailed = (runResult.data?.status === 'FAILED' ||
                            runResult.data?.status === 'failed' ||
                            runResult.data?.status === 'ERROR' ||
                            runResult.data?.state === 'FAILED' ||
                            runResult.status === 'FAILED');
                        console.log(`Run結果取得試行 ${attempts}/${maxAttempts}:`, {
                            status: runResult.data?.status,
                            messagesCount: runResult.data?.messages?.length || 0,
                            messageTypes: runResult.data?.messages?.map((msg) => msg.type) || [],
                            hasAIResponse,
                            hasOkWithFinalAI,
                            hasCompletedStatus,
                            isCompleted,
                            isFailed
                        });
                        // 🔍 【ポーリング継続/完了判定】
                        if (runResult && isCompleted) {
                            break;
                        }
                        else if (runResult && isFailed) {
                            throw new Error('AI実行が失敗しました');
                        }
                        else {
                            if (hasCompletedStatus && !hasAIResponse) {
                            }
                            else if (!hasCompletedStatus && hasAIResponse) {
                            }
                            else if (!hasCompletedStatus && !hasAIResponse) {
                            }
                            else {
                            }
                        }
                        // RUNNING状態の場合は継続ポーリング
                    }
                }
                if (!runResult || attempts >= maxAttempts) {
                    throw new Error('AI実行がタイムアウトしました');
                }
                // Step 5: Dive API専用レスポンス処理
                const responseTime = Date.now() - startTime;
                console.log('Dive エージェント実行レスポンス:', runResult);
                // チャット履歴に追加
                const userMessage = {
                    role: 'user',
                    message: chatMessage,
                    timestamp: new Date().toISOString()
                };
                // Dive APIレスポンスからAI応答テキスト抽出（Kusanagi実際構造対応）
                let responseText = '';
                // contentからテキストを抽出するヘルパー（string / Gemini array / 混合配列 に対応）
                // Geminiは content を以下の形式で返す:
                //   - string: "テキスト"
                //   - array: [{ type: "text", text: "..." }, "生の文字列", ...]  ← 混合あり
                const extractText = (content) => {
                    if (typeof content === 'string')
                        return content;
                    if (Array.isArray(content)) {
                        return content.map((item) => {
                            if (typeof item === 'string')
                                return item; // 生の文字列要素
                            if (item.type === 'text' || item.text)
                                return item.text || item.content || '';
                            return '';
                        }).join('');
                    }
                    return '';
                };
                if (runResult && runResult.data) {
                    if (runResult.data.messages && Array.isArray(runResult.data.messages)) {
                        // 最後の非空AIメッセージを取得（ツール呼出し用の空メッセージを除外）
                        const aiMessages = runResult.data.messages.filter((msg) => msg.type === 'ai' && extractText(msg.content).trim() !== '');
                        const aiMessage = aiMessages[aiMessages.length - 1]; // 最後のAIメッセージ
                        if (aiMessage && aiMessage.content) {
                            responseText = extractText(aiMessage.content);
                        }
                    }
                    if (!responseText) {
                        if (typeof runResult.data.output === 'string') {
                            responseText = runResult.data.output;
                        }
                        else if (typeof runResult.data.result === 'string') {
                            responseText = runResult.data.result;
                        }
                        else if (typeof runResult.data.response === 'string') {
                            responseText = runResult.data.response;
                        }
                        else if (typeof runResult.data === 'string') {
                            responseText = runResult.data;
                        }
                        else if (runResult.data.messages && runResult.data.messages.length > 0) {
                            const lastMessage = runResult.data.messages[runResult.data.messages.length - 1];
                            responseText = extractText(lastMessage.content) || lastMessage.text || JSON.stringify(lastMessage);
                        }
                        else {
                            responseText = JSON.stringify(runResult.data, null, 2);
                        }
                    }
                }
                else if (runResult) {
                    responseText = JSON.stringify(runResult, null, 2);
                }
                else {
                    responseText = 'AI応答の取得に失敗しました';
                }
                const agentMessage = {
                    role: 'agent',
                    message: responseText,
                    timestamp: new Date().toISOString(),
                    responseTime
                };
                setChatHistory(prev => [...prev, userMessage, agentMessage]);
                setChatMessage('');
                return; // Dive API処理完了で終了
            }
            else {
                // ローカルエージェント: 従来のエンドポイント使用
                console.log('ローカルエージェント実行: 従来API使用');
                response = await fetch(`/api/ai-agents/${selectedAgent.id}/execute`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        prompt: chatMessage.trim(),
                        parameters: {}
                    })
                });
            }
            if (!response.ok) {
                if (response.status === 408) {
                    throw new Error('エージェント処理がタイムアウトしました。Kusanagi APIの処理に時間がかかっています。');
                }
                throw new Error(`エージェント実行に失敗しました: ${response.status}`);
            }
            const result = await response.json();
            const responseTime = Date.now() - startTime;
            // レスポンス形式確認・デバッグ
            console.log('エージェント実行レスポンス:', result);
            if (result.status === 'success' || result.success) { // 両形式対応（Dive API: status, ローカル: success）
                // チャット履歴に追加
                const userMessage = {
                    role: 'user',
                    message: chatMessage,
                    timestamp: new Date().toISOString()
                };
                // Dive API と ローカルAPI のレスポンス形式に対応した抽出
                let responseText = '';
                if (isDiveAgent) {
                    // Diveエージェント用レスポンス抽出
                    if (typeof result.data === 'string') {
                        responseText = result.data;
                    }
                    else if (result.data && result.data.result) {
                        responseText = result.data.result;
                    }
                    else if (result.data && result.data.output) {
                        responseText = result.data.output;
                    }
                    else if (result.data && result.data.response) {
                        responseText = typeof result.data.response === 'string'
                            ? result.data.response
                            : JSON.stringify(result.data.response, null, 2);
                    }
                    else if (result.data && result.data.message) {
                        responseText = result.data.message;
                    }
                    else if (result.output) {
                        responseText = result.output;
                    }
                    else if (result.result) {
                        responseText = result.result;
                    }
                    else {
                        responseText = JSON.stringify(result.data || result, null, 2);
                    }
                }
                else {
                    // ローカルエージェント用レスポンス抽出（従来ロジック）
                    if (typeof result.data.response === 'string') {
                        responseText = result.data.response;
                    }
                    else if (result.data.response && result.data.response.output) {
                        responseText = result.data.response.output;
                    }
                    else if (result.data.response && result.data.response.message) {
                        responseText = result.data.response.message;
                    }
                    else {
                        responseText = JSON.stringify(result.data.response, null, 2);
                    }
                }
                const agentMessage = {
                    role: 'agent',
                    message: responseText,
                    timestamp: new Date().toISOString(),
                    responseTime: result.data.responseTime || responseTime
                };
                setChatHistory(prev => [...prev, userMessage, agentMessage]);
                setChatMessage('');
                // エージェントリストを更新（統計情報反映のため）
                loadDashboardData();
            }
            else {
                throw new Error(result.error || 'エージェント実行に失敗しました');
            }
        }
        catch (error) {
            console.error('エージェント実行エラー:', error);
            // エラーメッセージを詳細化
            let errorMsg = 'エージェント実行に失敗しました';
            if (error instanceof Error) {
                if (error.message.includes('404')) {
                    errorMsg = 'エージェントエンドポイントが見つかりません。エージェント設定を確認してください。';
                }
                else if (error.message.includes('401')) {
                    errorMsg = '認証エラーが発生しました。ログインし直してください。';
                }
                else if (error.message.includes('403')) {
                    errorMsg = 'このエージェントを実行する権限がありません。';
                }
                else if (error.message.includes('timeout')) {
                    errorMsg = 'エージェント処理がタイムアウトしました。AI処理に時間がかかっています。';
                }
                else {
                    errorMsg = `エラー: ${error.message}`;
                }
            }
            const errorMessage = {
                role: 'agent',
                message: errorMsg,
                timestamp: new Date().toISOString(),
                responseTime: Date.now() - startTime
            };
            setChatHistory(prev => [...prev, {
                    role: 'user',
                    message: chatMessage,
                    timestamp: new Date().toISOString()
                }, errorMessage]);
            setChatMessage('');
        }
        finally {
            setIsExecuting(false);
        }
    };
    const deleteAgent = (agent) => {
        // 削除確認ダイアログを開く（実際の削除は handleConfirmDeleteAgent）
        setAgentToDelete(agent);
    };
    const handleConfirmDeleteAgent = async () => {
        const agent = agentToDelete;
        if (!agent)
            return;
        setIsDeleting(true);
        try {
            // デバッグ: エージェント情報を出力
            console.log('削除対象エージェント:', {
                id: agent.id,
                name: agent.name,
                category: agent.category,
                isDive: agent.isDive,
                endpointPath: agent.endpointPath,
                endpoint: agent.endpoint
            });
            // Diveエージェント判定ログ
            const isDiveAgent = agent.isDive ||
                agent.category === 'Dive' ||
                !agent.endpointPath ||
                agent.endpointPath === '';
            console.log('Diveエージェント判定:', {
                isDiveFlag: agent.isDive,
                categoryCheck: agent.category === 'Dive',
                endpointCheck: !agent.endpointPath || agent.endpointPath === '',
                finalResult: isDiveAgent
            });
            const deleteEndpoint = isDiveAgent
                ? `/api/mizu/dive/agents/${agent.id}`
                : `/api/agents/${agent.id}`;
            console.log('削除エンドポイント:', deleteEndpoint);
            const response = await fetch(deleteEndpoint, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || `エージェント削除に失敗しました: ${response.status}`);
            }
            // レスポンス形式を確認（FuchiローカルAPIとMizu Dive APIで異なる）
            if (!result.success && result.status !== 'success') {
                throw new Error(result.error || result.message || 'エージェント削除に失敗しました');
            }
            // 成功時の処理
            console.log('エージェント削除成功:', result);
            // エージェント一覧を再読み込み
            await loadDashboardData();
            // 確認ダイアログを閉じる
            setAgentToDelete(null);
            // モーダルを閉じる（削除されたエージェントが選択されている場合）
            if (selectedAgent && selectedAgent.id === agent.id) {
                closeAgentDialog();
            }
            // 成功メッセージを表示（レスポンス形式に応じてメッセージを取得）
            let successMessage = result.data?.message || result.message || `エージェント「${agent.name}」が正常に削除されました`;
            // 英語メッセージを日本語に変換
            const englishToJapanese = {
                'Agent deleted': `エージェント「${agent.name}」が正常に削除されました`,
                'Agent removed': `エージェント「${agent.name}」が正常に削除されました`,
                'Success': `エージェント「${agent.name}」が正常に削除されました`,
                'Deleted successfully': `エージェント「${agent.name}」が正常に削除されました`
            };
            if (englishToJapanese[successMessage]) {
                successMessage = englishToJapanese[successMessage];
            }
            toast.success(successMessage);
        }
        catch (error) {
            console.error('エージェント削除エラー:', error);
            const errorMessage = error instanceof Error ? error.message : 'エージェントの削除に失敗しました';
            toast.error(errorMessage);
        }
        finally {
            setIsDeleting(false);
        }
    };
    const closeAgentDialog = () => {
        setShowAgentDialog(false);
        setSelectedAgent(null);
        setChatHistory([]);
        setChatMessage('');
    };
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" }) }));
    }
    if (!isAuthenticated) {
        return (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsxs("div", { className: "text-center", children: [_jsx(FaLock, { className: "mx-auto h-12 w-12 text-gray-400 mb-4" }), _jsx("h2", { className: "text-xl font-semibold text-gray-900 mb-2", children: "\u8A8D\u8A3C\u304C\u5FC5\u8981\u3067\u3059" }), _jsx("p", { className: "text-gray-600", children: "\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8AI\u30C0\u30C3\u30B7\u30E5\u30DC\u30FC\u30C9\u306B\u30A2\u30AF\u30BB\u30B9\u3059\u308B\u306B\u306F\u30ED\u30B0\u30A4\u30F3\u3057\u3066\u304F\u3060\u3055\u3044\u3002" })] }) }));
    }
    return (_jsxs(PageShell, { title: "\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8", icon: _jsx(FaRobot, { className: "text-indigo-600" }), actions: _jsxs("button", { onClick: () => setShowDiveModal(true), className: "bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center text-sm", "aria-label": "\u65B0\u3057\u3044\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8\u3092\u4F5C\u6210", title: "\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8\u3092\u4F5C\u6210\uFF08Kusanagi\u7D71\u5408\u30FBRAG/MCP\u5BFE\u5FDC\uFF09", children: [_jsx(FaPlus, { className: "mr-2", "aria-hidden": "true" }), "\u65B0\u898F\u4F5C\u6210"] }), toolbar: _jsx(AgentFilterBar, { searchTerm: searchTerm, setSearchTerm: setSearchTerm, selectedStatus: selectedStatus, setSelectedStatus: setSelectedStatus, sortBy: sortBy, setSortBy: setSortBy, sortOrder: sortOrder, setSortOrder: setSortOrder, filteredCount: filteredAgents.length, agentCount: agents.length }), children: [error && (_jsxs("div", { className: "mb-6 bg-red-50 border border-red-200 rounded-lg p-4", children: [_jsx("h3", { className: "text-red-800 font-medium", children: "\u30A8\u30E9\u30FC" }), _jsx("p", { className: "text-red-700 mt-2", children: error }), _jsx("button", { onClick: () => setError(''), className: "mt-2 text-red-600 hover:text-red-800", children: "\u9589\u3058\u308B" })] })), _jsx(AgentList, { paginatedAgents: paginatedAgents, filteredCount: filteredAgents.length, isDeleting: isDeleting, currentPage: currentPage, setCurrentPage: setCurrentPage, totalPages: totalPages, startIndex: startIndex, ITEMS_PER_PAGE: ITEMS_PER_PAGE, searchTerm: searchTerm, handleAgentSelect: handleAgentSelect, setEditingAgent: setEditingAgent, setShowEditModal: setShowEditModal, deleteAgent: deleteAgent }), _jsx(PopularAgentsRanking, { metrics: metrics }), showAgentDialog && selectedAgent && (_jsx(AgentDetailModal, { selectedAgent: selectedAgent, agentDialogMode: agentDialogMode, setAgentDialogMode: setAgentDialogMode, closeAgentDialog: closeAgentDialog, chatHistory: chatHistory, chatMessage: chatMessage, setChatMessage: setChatMessage, executeAgent: executeAgent, isExecuting: isExecuting })), _jsx(AgentModal, { isOpen: showDiveModal || showEditModal, onClose: () => {
                    setShowDiveModal(false);
                    setShowEditModal(false);
                    setEditingAgent(null);
                }, onSuccess: () => {
                    loadDashboardData();
                    setShowDiveModal(false);
                    setShowEditModal(false);
                    setEditingAgent(null);
                }, mode: showEditModal ? "edit" : "create", agentData: editingAgent || undefined }), _jsx(ConfirmDialog, { isOpen: !!agentToDelete, onClose: () => setAgentToDelete(null), onConfirm: handleConfirmDeleteAgent, danger: true, busy: isDeleting, title: "\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8\u524A\u9664\u306E\u78BA\u8A8D", confirmLabel: "\u524A\u9664", message: agentToDelete && (_jsxs("div", { className: "space-y-2", children: [_jsxs("p", { children: ["\u30A8\u30FC\u30B8\u30A7\u30F3\u30C8\u300C", agentToDelete.name, "\u300D\u3092\u524A\u9664\u3057\u3066\u3082\u3088\u308D\u3057\u3044\u3067\u3059\u304B\uFF1F"] }), _jsxs("p", { className: "text-gray-600", children: ["\u30A8\u30F3\u30C9\u30DD\u30A4\u30F3\u30C8: ", agentToDelete.endpointPath || agentToDelete.endpoint] }), _jsx("p", { className: "text-gray-600", children: "\u3053\u306E\u64CD\u4F5C\u306F\u5143\u306B\u623B\u305B\u307E\u305B\u3093\u3002" })] })) })] }));
};
export default HomePage;
